import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Grid, Tooltip, Typography } from '@mui/material';
import { styled as materialStyled } from '@mui/material/styles';
import { subordinateActions, SubordinatePermissions } from '../../../redux/subordinate/subordinateSlice';
import CheckSquareUnselectedSvg from '../../../assets/img/check-box-base.svg';
import CheckSquareOutlineSvg from '../../../assets/img/check-box-ticked.svg';
import { useAppDispatch } from '../../../redux/hooks';

const CustomGridItem = materialStyled(Grid)(() => ({
  overflowWrap: 'break-word',
  display: 'flex',
  alignItems: 'center',
}));

interface PermissionItemProps {
  permissionId: string;
  description: string;
  isChecked: boolean;
  isDisabled: boolean;
  tooltipMessage: string;
  onCheck: (_permissionId: string) => void;
}

const PermissionItem = React.memo(
  ({ permissionId, description, isChecked, isDisabled, tooltipMessage, onCheck }: PermissionItemProps) => (
    <CustomGridItem item xs={6}>
      <Tooltip title={tooltipMessage} placement="top" followCursor>
        <Box display="flex" alignItems="center">
          <Checkbox
            inputProps={{ 'aria-label': description }}
            checked={isChecked}
            onChange={() => onCheck(permissionId)}
            checkedIcon={<Box component="img" src={CheckSquareOutlineSvg} />}
            disabled={isDisabled}
            icon={<Box component="img" src={CheckSquareUnselectedSvg} />}
            edge="start"
          />
          <Typography
            variant="textBase"
            sx={{ color: isDisabled ? 'text.disabled' : 'text.primary' }}
            textTransform="capitalize"
          >
            {description}
          </Typography>
        </Box>
      </Tooltip>
    </CustomGridItem>
  ),
);

interface EditSubordinatePermissionsModelProps {
  permissions: SubordinatePermissions[] | undefined;
}

const EditSubordinatePermissionsModel = ({ permissions }: EditSubordinatePermissionsModelProps) => {
  const dispatch = useAppDispatch();
  const [checkedPermissions, setCheckedPermissions] = useState<string[]>([]);

  const permissionMap = useMemo(() => new Map(permissions?.map((p) => [p.permissionId ?? '', p])), [permissions]);

  const handleCheckboxClick = (permissionId: string) => {
    let updatedPermissions = checkedPermissions.includes(permissionId)
      ? checkedPermissions.filter((id) => id !== permissionId)
      : [...checkedPermissions, permissionId];

    updatedPermissions = updatedPermissions.filter((id) => {
      const permission = permissionMap.get(id);
      const dependencies = permission?.dependencies ?? [];
      if (dependencies.length === 0) return true;

      return dependencies.some((dep) => updatedPermissions.includes(dep.toString()));
    });
    setCheckedPermissions(updatedPermissions);
    dispatch(subordinateActions.setNewSubordinatePermissions(updatedPermissions));
  };

  useEffect(() => {
    if (permissions) {
      const initialPermissions = permissions
        .filter((permission) => permission.activeStatus)
        .map((permission) => permission.permissionId ?? '');
      setCheckedPermissions(initialPermissions);
    }
  }, [permissions]);

  return (
    <Grid container mt="12px">
      <Grid item xs={12}>
        <Typography variant="textBase500">Permissions</Typography>
      </Grid>
      {permissions?.map((permission) => {
        const permissionId = permission.permissionId ?? '';
        const dependencies = permission.dependencies?.map((d) => d.toString()) ?? [];
        const isDisabled = dependencies.length > 0 && !dependencies.some((d) => checkedPermissions.includes(d));

        const tooltipDependencies = dependencies
          .map((d) => permissionMap.get(d))
          .filter(Boolean) as SubordinatePermissions[];

        const tooltipMessage =
          tooltipDependencies.length > 0 && isDisabled
            ? `Requires one of the following permissions: ${tooltipDependencies
                .map((d) => d.permissionDescription)
                .join(', ')}`
            : '';

        return (
          <PermissionItem
            key={permissionId}
            permissionId={permissionId}
            description={permission.permissionDescription ?? ''}
            isChecked={checkedPermissions.includes(permissionId)}
            isDisabled={isDisabled}
            tooltipMessage={tooltipMessage}
            onCheck={handleCheckboxClick}
          />
        );
      })}
    </Grid>
  );
};

export default EditSubordinatePermissionsModel;
